import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CheckoutPayload,
  CheckoutRespuesta,
  Promocion,
  SuscripcionSinPagoRespuesta,
} from '../../features/marketplace/paquete.model';
import { Sesion } from '../auth/sesion.model';

export interface PaquetesEmpresaRespuesta {
  paquetesVigentes: Record<string, unknown>[];
  addOns: Record<string, unknown>[];
  configuracionSuscripcion?: {
    id?: number;
    tipoPantalla?: string;
    modalidadPago?: { id?: number; clave?: string; nombre?: string } | null;
  } | null;
}

@Service()
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api_url;

  /** Canjea el token de un solo uso de la URL por una sesion completa. */
  obtenerSesionTokenizada(token: string): Observable<Sesion> {
    return this.http.post<Sesion>(`${this.url}api/commons/obtener-sesion-tokenizada`, token);
  }

  cerrarSesion(token: string): Observable<unknown> {
    return this.http.post(`${this.url}api/commons/cerrar-sesion`, { token });
  }

  instalarDemo(aplicacionId: number, usuariosIds: number[]): Observable<{ urlAplicacion?: string | null }> {
    return this.http.post<{ urlAplicacion?: string | null }>(
      `${this.url}api/aplicaciones/instalar-demo`,
      { aplicacionId, usuariosIds }
    );
  }

  getEmpresasPaquetesPorEmpresa(): Observable<Record<string, unknown>[]> {
    return this.http.get<Record<string, unknown>[]>(
      `${this.url}api/suscripciones/EmpresasPaquetesBuscarPorEmpresa`
    );
  }

  getEmpresaCicloCobros(empresaId: number): Observable<{
    cicloCobroVigente?: {
      fechaInicial?: string;
      fechaFinal?: string;
      [key: string]: unknown;
    };
    esProrrateado?: boolean;
    diasRestantes?: number;
    diasPeriodoCompleto?: number;
    factorProrrateo?: number;
    zonaHoraria?: string;
    [key: string]: unknown;
  } | null> {
    return this.http.get<any>(
      `${this.url}api/facturacion/buscarEmpresasConCicloCobroVigentePorEmpresaId/${empresaId}`
    );
  }

  getPaquetesParaEmpresas(
    empresaId: number,
    aplicacionId: string | number
  ): Observable<PaquetesEmpresaRespuesta> {
    return this.http.get<PaquetesEmpresaRespuesta>(
      `${this.url}api/suscripciones/PaquetesBuscarParaEmpresa/${empresaId}`,
      {
        params: {
          aplicacionId: String(aplicacionId),
          validaVersiones: 'false',
        },
      }
    );
  }

  getPromocionesBuscarPorReferencia(
    referencia: string,
    modalidadPagoId: number,
    paqueteId: number
  ): Observable<Promocion | null> {
    return this.http.get<Promocion | null>(
      `${this.url}api/promociones/PromocionesBuscarPorReferencia/${referencia}/${modalidadPagoId}/${paqueteId}`,
      {
        headers: new HttpHeaders({
          'x-skip-interceptor': 'true',
          'x-skip-toast': 'true',
        }),
      }
    );
  }

  createCheckoutSession(data: CheckoutPayload): Observable<CheckoutRespuesta> {
    return this.http.post<CheckoutRespuesta>(`${this.url}api/pay/createCheckoutSession`, data);
  }

  /**
   * Registra la suscripción sin cargo en Stripe cuando el monto es menor o igual
   * al mínimo (100 MXN).
   */
  registrarSinPago(data: CheckoutPayload): Observable<SuscripcionSinPagoRespuesta> {
    return this.http.post<SuscripcionSinPagoRespuesta>(
      `${this.url}api/pay/registrar-sin-pago`,
      data,
    );
  }

  verificarPagoExitoso(sessionId: string): Observable<unknown> {
    return this.http.get(`${this.url}api/pay/verificar-pago-exitoso/${sessionId}`);
  }
}
